// Pure traversal utilities for the dynamic enrollment-invite form.
//
// The enrollment scenario graph is a dict of nodes keyed by id. We walk it from
// the StartNode following nextNodes, collecting the data-collection nodes the
// user must fill. At an IfConditionNode we resolve the branch from the answers
// gathered so far; at a goto we jump. Walking STOPS at the first branch we can't
// resolve yet (its source question is still unanswered) — that's how the form
// reveals further questions only once the deciding answer exists.

export const DATA_NODE_TYPES = [
  "message:input",
  "message:single",
  "message:multi",
  "message:date",
  "message:time",
  "message:picker",
  "message:slider",
];

function findStartId(graph) {
  const entry = Object.values(graph || {}).find((n) => n && n.type === "StartNode");
  return entry ? entry.nextNodes && entry.nextNodes[0] : undefined;
}

// Candidate strings an answer could match a condition value against (we compare
// by both option value and label to tolerate either authoring convention).
function answerCandidates(answer) {
  if (answer == null) {return [];}
  if (typeof answer === "string") {return [answer];}
  const out = [];
  if (answer.value != null) {out.push(String(answer.value));}
  if (answer.label != null) {out.push(String(answer.label));}
  if (Array.isArray(answer.answers)) {
    for (const a of answer.answers) {
      if (a == null) {continue;}
      if (a.value != null) {out.push(String(a.value));}
      if (a.label != null) {out.push(String(a.label));}
    }
  }
  return out;
}

// Returns the branch index for an IfConditionNode, or null if it can't be
// resolved yet (the source question has no answer).
export function matchBranchIndex(ifNode, answers) {
  const data = (ifNode && ifNode.data) || {};
  const statements = Array.isArray(data.condStatements) ? data.condStatements : [];
  const sourceAnswer = answers[data.condSourceId];

  const elseIndex = statements.findIndex((s) => s && s.op === "else");

  if (sourceAnswer == null) {
    return null; // wait until the source question is answered
  }

  const candidates = answerCandidates(sourceAnswer);
  for (let i = 0; i < statements.length; i++) {
    const stmt = statements[i];
    if (!stmt || stmt.op === "else") {continue;}
    const values = Array.isArray(stmt.value) ? stmt.value.map((v) => String(v && v.value)) : [];
    if (values.some((v) => candidates.includes(v))) {
      return i;
    }
  }
  return elseIndex !== -1 ? elseIndex : null;
}

// Walk the graph given current answers. Returns the ordered list of data-node
// ids the user should see, and whether the path reached the Finish node.
export function resolvePath(graph, answers = {}) {
  const path = [];
  const visited = new Set();
  let currentId = findStartId(graph);

  while (currentId) {
    if (visited.has(currentId)) {break;} // cycle guard (e.g. goto loop)
    visited.add(currentId);

    const node = graph[currentId];
    if (!node) {break;}

    const type = node.type;
    const nodeType = node.data && node.data.nodeType;

    if (type === "FinishNode") {
      return { nodeIds: path, complete: true };
    }

    if (nodeType === "message:goto") {
      currentId = node.data && node.data.selectedTarget && node.data.selectedTarget.value;
      continue;
    }

    if (type === "IfConditionNode") {
      const idx = matchBranchIndex(node, answers);
      if (idx == null) {
        return { nodeIds: path, complete: false }; // need an answer to continue
      }
      const branchId = (node.nextNodes || [])[idx];
      const branchNode = graph[branchId];
      currentId = branchNode && branchNode.nextNodes && branchNode.nextNodes[0];
      continue;
    }

    if (type === "IfBranchNode" || type === "IfCloseNode") {
      currentId = node.nextNodes && node.nextNodes[0];
      continue;
    }

    // Data-collection node — recognized by data.nodeType regardless of the
    // render `type` (seeded/migrated nodes use "MessageInfoNode"; nodes added in
    // the editor are serialized as "GenericNode").
    if (DATA_NODE_TYPES.includes(nodeType)) {
      path.push(currentId);
    }

    currentId = node.nextNodes && node.nextNodes[0];
  }

  return { nodeIds: path, complete: false };
}

// Build the signupSubject payload pieces from the resolved path + answers.
// Maps node answers to EnrollmentField ids via the materialized fields (which
// carry nodeId). The email node maps to subject.email, not to a field.
export function buildInvitePayload(graph, fields, answers, pathNodeIds) {
  const fieldByNodeId = {};
  for (const f of fields || []) {
    if (f && f.nodeId) {fieldByNodeId[f.nodeId] = f;}
  }

  let email = "";
  const customFields = {};

  for (const nodeId of pathNodeIds) {
    const node = graph[nodeId];
    if (!node) {continue;}
    const alias = node.data && node.data.alias;
    const answer = answers[nodeId];

    if (alias === "email") {
      email = typeof answer === "string" ? answer : (answer && answer.value) || "";
      continue;
    }

    const field = fieldByNodeId[nodeId];
    if (!field || answer == null) {continue;}

    if (field.type === "select") {
      if (node.data && node.data.nodeType === "message:multi") {
        // multiple choice -> array of option ids (backend creates one response each)
        customFields[field.id] = Array.isArray(answer && answer.answers)
          ? answer.answers.map((a) => a.value)
          : [];
      } else {
        // single choice -> option id (response option value === EnrollmentFieldOption id)
        customFields[field.id] = answer && answer.value != null ? answer.value : answer;
      }
    } else {
      // input / date / time -> plain string value
      customFields[field.id] = typeof answer === "string" ? answer : (answer && answer.value) || "";
    }
  }

  return { email, customFields };
}
